/**
 * useChat — Core chat state and message management.
 *
 * Owns: messages, question, chatId, sendMessage (streaming + non-streaming),
 * startNewChat, reset. Depends on useAssistant for retrieval config.
 */

import { useState, useCallback, useRef, useEffect } from "react";
import { fetch_stream } from "../utils/streaming";
import { createStreamHandlers } from "./useStreamProcessor";

const MAX_RESP_STATUS = 300;
const POLL_INTERVAL_MS = 30000;
const MAX_POLL_RETRIES = 3;

export const useChat = ({
  // From useAssistant
  retrievalId,
  streamingAvailable,
  asyncAvailable,
  iniMessage,
  // Pre-existing chat (from props.chat)
  initialChat = null,
  // API functions (props)
  addQuestionRetData,
  addMessage,
  addQuestionAsync,
  addMessageAsync,
  getChat,
  getRetrieval,
  // Assistant helpers
  resetAssistantStates: resetAssistant,
  setLoading: setAssistantLoading,
  setAllowAsk,
  getCachedAssistant,
  applyAssistantData,
  // Callback for feedback reset (component-layer concern)
  onResetFeedback,
  // Error handler
  showStatusModal,
  // Streaming error state
  setStreamingError,
  setStatusModal,
}) => {
  // Chat state — initialize from pre-existing chat if provided
  const [question, setQuestion] = useState(initialChat || null);
  const [messages, setMessages] = useState(
    initialChat
      ? initialChat.messages.map((msg) => ({ ...msg, attachment: null }))
      : [],
  );
  const [chatId, setChatId] = useState(initialChat?.id || null);
  const [inputValue, setInputValue] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [ended, setEnded] = useState(
    initialChat ? initialChat.execution_status === "ENDED" : false,
  );
  const [executionStatus, setExecutionStatus] = useState(
    initialChat ? initialChat.execution_status : null,
  );
  const [cantResponses, setCantResponses] = useState(1);
  const [endedStatus, setEndedStatus] = useState(0);
  const [waitingOAuthServerId, setWaitingOAuthServerId] = useState(null);
  const [customErrorMessage, setCustomErrorMessage] = useState(null);
  // Shown for every message sent in async mode, until the poll delivers a
  // terminal status for that message.
  const [showAsyncNotice, setShowAsyncNotice] = useState(false);

  // Ref for reading messages inside sendMessage without adding it to deps
  const messagesRef = useRef(messages);
  messagesRef.current = messages;

  // Status helpers
  const isEndedStatus = useCallback((status) => {
    return [
      "ENDED",
      "ERROR",
      "ENDED_MAX_INTERACTIONS",
      "FORCED_ENDED",
    ].includes(status);
  }, []);

  const isErrorStatus = useCallback((status) => {
    return ["ERROR", "ENDED_MAX_INTERACTIONS", "FORCED_ENDED"].includes(status);
  }, []);

  const handleExecutionStatus = useCallback(
    (status) => {
      const conversationEnded = isEndedStatus(status);
      setEnded(conversationEnded);
      setExecutionStatus(status);

      if (isErrorStatus(status)) {
        showStatusModal(status);
      }
    },
    [isEndedStatus, isErrorStatus, showStatusModal],
  );

  const requestStatus = useCallback((status) => {
    if (status > MAX_RESP_STATUS) {
      setEndedStatus(status);
    }
  }, []);

  // the backend persists it instead on the reauth
  // assistant message's metadata (metadata: { server_id, type: "mcp_oauth_required" }).
  // Only used as a fallback when data.server_id is absent (async).
  const getReauthServerIdFromMessages = useCallback((messages) => {
    if (!Array.isArray(messages)) return undefined;
    for (let i = messages.length - 1; i >= 0; i--) {
      const msg = messages[i];
      if (msg.role === "assistant" && msg.metadata?.type === "mcp_oauth_required") {
        return msg.metadata.server_id;
      }
    }
    return undefined;
  }, []);

  // Apply a chat object (from a sync POST response or a polled GET) to state.
  // Returns true if an OAuth-related special case was handled (caller should
  // stop, e.g. clear isLoading and not run its own execution-status logic).
  const applyChatResponseData = useCallback(
    (data) => {
      if (data?.mcp_oauth_required) {
        setQuestion(data);
        setChatId(data.chat_id ?? data.id);
        setWaitingOAuthServerId(data.server_id);
        handleExecutionStatus("WAITING_FOR_AUTH");
        setMessages(
          iniMessage ? (data.messages ?? []).slice(1) : data.messages ?? [],
        );
        return true;
      }

      if (data?.execution_status === "WAITING_FOR_AUTH") {
        setQuestion(data);
        setChatId(data.id);
        setWaitingOAuthServerId(
          data.server_id ?? getReauthServerIdFromMessages(data.messages),
        );
        handleExecutionStatus(data.execution_status);
        setMessages(iniMessage ? data.messages.slice(1) : data.messages);
        return true;
      }

      setQuestion(data);
      setChatId(data.id);

      const currentMessages = messagesRef.current;
      const updatedMessages = data.messages.map((msg, index) => {
        if (index < currentMessages.length) {
          const originalMsg = currentMessages[index];
          return {
            ...msg,
            ...(originalMsg.attachment && {
              attachment: originalMsg.attachment,
            }),
            ...(originalMsg.audioBlob && {
              audioBlob: originalMsg.audioBlob,
              audioDuration: originalMsg.audioDuration,
              isAudioMessage: true,
            }),
          };
        }
        return msg;
      });

      setMessages(iniMessage ? updatedMessages.slice(1) : updatedMessages);
      return false;
    },
    [iniMessage, handleExecutionStatus, getReauthServerIdFromMessages],
  );

  // Async chat polling — recursive setTimeout (not setInterval) so a slow
  // GET can't overlap with the next tick. A generation counter invalidates
  // any in-flight/pending tick after a reset, a new chat, or unmount, so a
  // stale poll response can never clobber newer state.
  const pollTimeoutRef = useRef(null);
  const pollGenerationRef = useRef(0);

  const stopPolling = useCallback(() => {
    pollGenerationRef.current += 1;
    if (pollTimeoutRef.current) {
      clearTimeout(pollTimeoutRef.current);
      pollTimeoutRef.current = null;
    }
  }, []);

  const pollChat = useCallback(
    (id) => {
      stopPolling();
      const generation = pollGenerationRef.current;
      let pollRetries = 0;

      const tick = async () => {
        try {
          const data = await getChat(id);
          if (generation !== pollGenerationRef.current) return;
          pollRetries = 0;

          if (data.execution_status === "RUNNING") {
            pollTimeoutRef.current = setTimeout(tick, POLL_INTERVAL_MS);
            return;
          }

          pollTimeoutRef.current = null;
          const handledSpecialCase = applyChatResponseData(data);
          if (!handledSpecialCase) {
            handleExecutionStatus(data.execution_status);
          }
          setIsLoading(false);
          setShowAsyncNotice(false);
        } catch (error) {
          if (generation !== pollGenerationRef.current) return;

          if (pollRetries < MAX_POLL_RETRIES) {
            pollRetries += 1;
            pollTimeoutRef.current = setTimeout(tick, POLL_INTERVAL_MS);
            return;
          }

          pollTimeoutRef.current = null;
          setMessages((prev) => {
            const last = prev[prev.length - 1];
            return last?.role === "assistant" && last?.loading
              ? prev.slice(0, -1)
              : prev;
          });
          setIsLoading(false);
          setCustomErrorMessage(error?.customMessage || null);
          setExecutionStatus("ERROR");
          setEnded(true);
          setShowAsyncNotice(false);
          showStatusModal(null, error?.customMessage);
        }
      };

      pollTimeoutRef.current = setTimeout(tick, POLL_INTERVAL_MS);
    },
    [getChat, applyChatResponseData, stopPolling, handleExecutionStatus, showStatusModal],
  );

  // Stop any in-flight poll on unmount
  useEffect(() => stopPolling, [stopPolling]);

  // Terminal handler for a failed chat request (send message or start chat),
  // only for genuine request errors (rejected promise / httpError / network),
  // NOT for successful terminal statuses, where the assistant may still be 
  // streaming its final answer.
  const handleRequestError = useCallback(
    (error) => {
      console.error("Chat request error:", error);
      // Drop the trailing empty "thinking" bubble if it's still loading.
      setMessages((prev) => {
        const last = prev[prev.length - 1];
        return last?.role === "assistant" && last?.loading
          ? prev.slice(0, -1)
          : prev;
      });
      setIsLoading(false);
      setCustomErrorMessage(error?.customMessage || null);
      setExecutionStatus("ERROR");
      setEnded(true);
      setStatusModal(true);
    },
    [setStatusModal],
  );

  // Preserve audio data when messages are replaced by server response
  const preserveAudioInMessages = useCallback((newMessages, currentMessages) => {
    return newMessages.map((msg, index) => {
      if (
        index < currentMessages.length &&
        currentMessages[index].isAudioMessage
      ) {
        return {
          ...msg,
          audioBlob: currentMessages[index].audioBlob,
          audioDuration: currentMessages[index].audioDuration,
          isAudioMessage: true,
        };
      }
      return msg;
    });
  }, []);

  // Reset all chat state
  const resetChatStates = useCallback(() => {
    stopPolling();
    setQuestion(null);
    setMessages([]);
    setChatId(null);
    setInputValue("");
    setIsLoading(false);
    setEnded(false);
    setExecutionStatus(null);
    setCantResponses(1);
    setEndedStatus(0);
    setWaitingOAuthServerId(null);
    setCustomErrorMessage(null);
    setShowAsyncNotice(false);
  }, [stopPolling]);

  // Start new chat
  const startNewChat = useCallback(async () => {
    if (!retrievalId) return;

    setAssistantLoading(true);
    resetChatStates();
    if (resetAssistant) resetAssistant();

    if (onResetFeedback) onResetFeedback();

    // Try cached data first for immediate response, then fetch full data
    const cached = getCachedAssistant(retrievalId);
    if (cached) {
      applyAssistantData(cached);
      setAllowAsk(true);
      setAssistantLoading(false);
      return;
    }

    // Fallback: fetch individually
    try {
      const data = await getRetrieval(retrievalId);
      applyAssistantData(data);
      setAllowAsk(true);
    } catch (error) {
      handleRequestError(error);
    } finally {
      setAssistantLoading(false);
    }
  }, [
    retrievalId,
    resetChatStates,
    resetAssistant,
    onResetFeedback,
    getCachedAssistant,
    getRetrieval,
    applyAssistantData,
    handleRequestError,
    setAssistantLoading,
    setAllowAsk,
  ]);

  // Build metadata body for file uploads
  const buildFileMetadataBody = useCallback(
    (streamBody, messageFiles, filesMetadata, fileSpecificMetadata) => {
      messageFiles.forEach((file, index) => {
        streamBody[`file_${index + 1}`] = file;
      });

      if (
        messageFiles.length > 0 &&
        (filesMetadata.length > 0 ||
          Object.keys(fileSpecificMetadata).length > 0)
      ) {
        const globalMetadataObject = {};
        filesMetadata.forEach((card) => {
          if (card.key && card.values) {
            globalMetadataObject[card.key] =
              card.values.length === 1 ? card.values[0] : card.values;
          }
        });

        messageFiles.forEach((file, index) => {
          const fileMetadataObject = { ...globalMetadataObject };
          const specificMetadata = fileSpecificMetadata[file.name];
          if (specificMetadata && specificMetadata.length > 0) {
            specificMetadata.forEach((card) => {
              if (card.key && card.values) {
                fileMetadataObject[card.key] =
                  card.values.length === 1 ? card.values[0] : card.values;
              }
            });
          }
          if (Object.keys(fileMetadataObject).length > 0) {
            streamBody[`metadata_${index + 1}`] =
              JSON.stringify(fileMetadataObject);
          }
        });
      }
    },
    [],
  );

  // Send message (streaming + non-streaming)
  const sendMessage = useCallback(
    async ({
      messageFiles = [],
      filesMetadata = [],
      fileSpecificMetadata = {},
      metadata = { chat: {}, message: {} },
      onMessageSent,
    } = {}) => {
      if (inputValue.trim() === "" && messageFiles.length === 0) return;
      setEndedStatus(0);

      const messageToSend =
        inputValue.trim() === "" && messageFiles.length > 0 ? "" : inputValue;

      // Build user message
      // Convert global + per-file metadata to flat {filename: {key: value}} for optimistic display.
      const globalFlat = {};
      filesMetadata.forEach((card) => {
        if (card.key && card.values) {
          globalFlat[card.key] =
            card.values.length === 1 ? card.values[0] : card.values;
        }
      });
      const flatFileMetadata = {};
      messageFiles.forEach((file) => {
        const merged = { ...globalFlat };
        const cards = fileSpecificMetadata[file.name];
        if (Array.isArray(cards)) {
          cards.forEach((card) => {
            if (card.key && card.values) {
              merged[card.key] =
                card.values.length === 1 ? card.values[0] : card.values;
            }
          });
        }
        if (Object.keys(merged).length > 0) flatFileMetadata[file.name] = merged;
      });
      const userMessage = {
        content: messageToSend,
        role: "user",
        usr_msg_num: cantResponses,
        attachment: messageFiles.length > 0 ? messageFiles : null,
        metadata_files:
          Object.keys(flatFileMetadata).length > 0
            ? flatFileMetadata
            : undefined,
      };

      // Prepare metadata
      const hasMessageMetadata =
        metadata.message && Object.keys(metadata.message).length > 0;
      const messageMetadataToSend = hasMessageMetadata
        ? { ...metadata.message }
        : null;
      const chatMetadataToSend =
        metadata.chat && Object.keys(metadata.chat).length > 0
          ? { ...metadata.chat }
          : null;

      setCantResponses((prev) => prev + 1);
      setMessages((prev) => [...prev, userMessage]);
      setInputValue("");
      setIsLoading(true);

      // Loading message
      setMessages((prevMessages) => [
        ...prevMessages,
        { content: "", role: "assistant", loading: true },
      ]);

      // Notify parent to clear files/metadata
      if (onMessageSent) onMessageSent();

      try {
        if (asyncAvailable) {
          // ASYNC MODE
          try {
            const existingChatId = question?.id ?? chatId;
            const response = existingChatId != null
              ? await addMessageAsync(
                  messageToSend,
                  existingChatId,
                  messageFiles.length > 0 ? messageFiles : null,
                  messageMetadataToSend,
                  filesMetadata.length > 0 ? filesMetadata : null,
                  Object.keys(fileSpecificMetadata).length > 0
                    ? fileSpecificMetadata
                    : null,
                )
              : await addQuestionAsync(
                  messageToSend,
                  retrievalId,
                  messageFiles.length > 0 ? messageFiles : null,
                  chatMetadataToSend,
                  messageMetadataToSend,
                  filesMetadata.length > 0 ? filesMetadata : null,
                  Object.keys(fileSpecificMetadata).length > 0
                    ? fileSpecificMetadata
                    : null,
                );

            const newChatId = response.data?.chat_id;
            if (!newChatId) {
              throw new Error("Async response did not include a chat_id");
            }

            // Shown every time a message is sent in async mode, cleared once
            // the poll delivers a terminal status for this message.
            setShowAsyncNotice(true);

            setChatId(newChatId);
            pollChat(newChatId);
          } catch (error) {
            console.error("Error:", error);
            showStatusModal(null, error?.customMessage);
            setIsLoading(false);
            setShowAsyncNotice(false);
          }
          return;
        } else if (streamingAvailable) {
          // STREAMING MODE
          const streamBody = { message: messageToSend };

          if (question === null && chatMetadataToSend) {
            streamBody.chat_metadata = JSON.stringify(chatMetadataToSend);
          }
          if (messageMetadataToSend) {
            streamBody.metadata = JSON.stringify(messageMetadataToSend);
          }
          buildFileMetadataBody(
            streamBody,
            messageFiles,
            filesMetadata,
            fileSpecificMetadata,
          );

          const endpoint =
            question === null
              ? `cognitive/assistants/${retrievalId}/chats_stream/`
              : `cognitive/chats_stream/${question.id}/`;

          const handlers = createStreamHandlers({
            setMessages: (updaterOrValue) => {
              if (typeof updaterOrValue === "function") {
                setMessages((prev) => {
                  const result = updaterOrValue(prev);
                  return preserveAudioInMessages(result, prev);
                });
              } else {
                // useStreamProcessor already handles iniMessage slicing — don't slice again
                setMessages((prev) =>
                  preserveAudioInMessages(updaterOrValue, prev),
                );
              }
            },
            setQuestion,
            setChatId,
            handleExecutionStatus,
            setIsLoading,
            setWaitingOAuthServerId,
            iniMessage,
            onRequestError: handleRequestError,
          });

          fetch_stream(
            endpoint,
            streamBody,
            handlers.onChunk,
            () => {},
            (error) =>
              handlers.onError(error, setStreamingError, setStatusModal),
          );
        } else {
          // NON-STREAMING MODE
          let response = null;

          if (question != null) {
            response = await addMessage(
              messageToSend,
              question.id,
              messageFiles.length > 0 ? messageFiles : null,
              messageMetadataToSend,
              filesMetadata.length > 0 ? filesMetadata : null,
              Object.keys(fileSpecificMetadata).length > 0
                ? fileSpecificMetadata
                : null,
            );
          } else {
            response = await addQuestionRetData(
              messageToSend,
              retrievalId,
              messageFiles.length > 0 ? messageFiles : null,
              chatMetadataToSend,
              messageMetadataToSend,
              filesMetadata.length > 0 ? filesMetadata : null,
              Object.keys(fileSpecificMetadata).length > 0
                ? fileSpecificMetadata
                : null,
            );
          }

          // Check for MCP OAuth / waiting-for-auth (shared with polling)
          const handledSpecialCase = applyChatResponseData(response.data);
          if (handledSpecialCase) {
            setIsLoading(false);
            return;
          }

          setIsLoading(false);

          if (response.status) {
            requestStatus(response.status);
            if (response.status > MAX_RESP_STATUS) {
              setEnded(true);
              showStatusModal();
            } else {
              handleExecutionStatus(response.data.execution_status);
            }
          }
        }
      } catch (error) {
        handleRequestError(error);
      } finally {
        if (!streamingAvailable && !asyncAvailable) {
          setIsLoading(false);
        }
      }
    },
    [
      inputValue,
      cantResponses,
      question,
      chatId,
      retrievalId,
      streamingAvailable,
      asyncAvailable,
      iniMessage,
      addQuestionRetData,
      addMessage,
      addQuestionAsync,
      addMessageAsync,
      pollChat,
      applyChatResponseData,
      buildFileMetadataBody,
      preserveAudioInMessages,
      handleExecutionStatus,
      requestStatus,
      showStatusModal,
      handleRequestError,
      setStreamingError,
      setStatusModal,
    ],
  );

  return {
    // State
    question,
    setQuestion,
    messages,
    setMessages,
    chatId,
    setChatId,
    inputValue,
    setInputValue,
    isLoading,
    setIsLoading,
    ended,
    setEnded,
    executionStatus,
    endedStatus,
    waitingOAuthServerId,
    setWaitingOAuthServerId,
    customErrorMessage,
    setCustomErrorMessage,
    showAsyncNotice,

    // Handlers
    sendMessage,
    startNewChat,
    resetChatStates,
    handleExecutionStatus,
  };
};
